import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Shield, ShieldAlert, ShieldCheck, ShieldQuestion, Loader2, Clock, CheckCircle, XCircle, Download, Trash2, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const API = '/api';

function StatusBadge({ status }) {
  const map = {
    pending: { cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: Clock, label: 'Pending' },
    processing: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2, label: 'Checking...' },
    completed: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Done' },
    failed: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, label: 'Failed' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs font-medium border ${s.cls}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {s.label}
    </Badge>
  );
}

function DomainVerdictBadge({ status, severity }) {
  if (status === 'clean') {
    return (
      <Badge variant="outline" className="gap-1 text-xs border-emerald-200 bg-emerald-50 text-emerald-700">
        <ShieldCheck className="w-3 h-3" /> Clean
      </Badge>
    );
  }
  if (status === 'flagged') {
    const cls = severity === 'high'
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : 'border-amber-200 bg-amber-50 text-amber-700';
    return (
      <Badge variant="outline" className={`gap-1 text-xs ${cls}`}>
        <ShieldAlert className="w-3 h-3" /> Flagged
      </Badge>
    );
  }
  if (status === 'no_data') {
    return (
      <Badge variant="outline" className="gap-1 text-xs border-zinc-200 bg-zinc-50 text-zinc-500">
        <ShieldQuestion className="w-3 h-3" /> No Data
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-xs border-zinc-200 bg-zinc-50 text-zinc-500">
      <AlertTriangle className="w-3 h-3" /> Error
    </Badge>
  );
}

function IssueTypeBadge({ type }) {
  const map = {
    asian_language: { cls: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Asian Lang' },
    casino: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Casino' },
    adult: { cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Adult' },
  };
  const s = map[type] || { cls: 'bg-zinc-50 text-zinc-600 border-zinc-200', label: type };
  return <Badge variant="outline" className={`text-xs ${s.cls}`}>{s.label}</Badge>;
}

function DomainDetailRow({ result }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-zinc-100">
      <div
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 cursor-pointer transition-colors"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />}
        <span className="font-mono text-xs text-zinc-700 min-w-[160px]">{result.domain}</span>
        <DomainVerdictBadge status={result.status} severity={result.severity} />
        <span className="text-xs text-zinc-400 ml-auto">{result.snapshots_checked}/{result.snapshots_available} snapshots</span>
      </div>
      {open && (
        <div className="pl-10 pr-4 pb-3 space-y-2">
          {result.issues && result.issues.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-zinc-500">Issues found:</span>
              {result.issues.map((issue, i) => (
                <div key={i} className="flex items-start gap-2 pl-2">
                  <IssueTypeBadge type={issue.type} />
                  <span className="text-xs text-zinc-600">{issue.detail}</span>
                </div>
              ))}
            </div>
          )}
          {result.snapshot_details && result.snapshot_details.length > 0 && (
            <div className="space-y-1 mt-2">
              <span className="text-xs font-medium text-zinc-500">Snapshots:</span>
              {result.snapshot_details.map((snap, i) => (
                <div key={i} className="flex items-center gap-2 pl-2 text-xs">
                  {snap.clean ? (
                    <ShieldCheck className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <ShieldAlert className="w-3 h-3 text-rose-500 flex-shrink-0" />
                  )}
                  <span className="text-zinc-500 font-mono">{snap.year}</span>
                  <span className="text-zinc-400 truncate max-w-[300px]">{snap.url}</span>
                  {snap.issues && snap.issues.length > 0 && (
                    <span className="text-rose-500">{snap.issues.length} issue(s)</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {result.status === 'no_data' && (
            <p className="text-xs text-zinc-400 pl-2">No snapshots found in Wayback Machine for this domain</p>
          )}
          {result.error && (
            <p className="text-xs text-rose-500 pl-2">{result.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function HistoryCheckPage() {
  const [domainsText, setDomainsText] = useState('');
  const [checks, setChecks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  const fetchChecks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/history-checks`, { withCredentials: true });
      setChecks(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    if (!selectedCheckId) { setDetail(null); return; }
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        const { data } = await axios.get(`${API}/history-check/${selectedCheckId}`, { withCredentials: true });
        if (!cancelled) setDetail(data);
      } catch (err) { console.error(err); }
    };
    fetchDetail();
    const interval = setInterval(fetchDetail, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedCheckId]);

  useEffect(() => {
    fetchChecks();
    const iv = setInterval(fetchChecks, 10000);
    return () => clearInterval(iv);
  }, [fetchChecks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const domains = domainsText.split('\n').map(d => d.trim()).filter(Boolean);
    if (domains.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/history-check`, { domains }, { withCredentials: true });
      setDomainsText('');
      toast.success(`Checking history for ${domains.length} domain(s)`);
      setSelectedCheckId(data.id);
      fetchChecks();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to start');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/history-check/${id}`, { withCredentials: true });
      if (selectedCheckId === id) setSelectedCheckId(null);
      toast.success('Deleted');
      fetchChecks();
    } catch { toast.error('Failed'); }
  };

  const handleDownload = (check) => {
    if (!check?.clean_domains?.length) return;
    const text = check.clean_domains.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clean_domains.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredResults = detail?.results?.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'clean') return r.status === 'clean';
    if (filter === 'flagged') return r.status === 'flagged';
    if (filter === 'no_data') return r.status === 'no_data';
    return true;
  }) || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="history-check-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-1 space-y-5">
            <div className="border border-zinc-200 bg-white p-5" data-testid="history-input-section">
              <h2 className="text-lg font-bold tracking-tight font-heading text-zinc-950 mb-1">
                <Shield className="w-5 h-5 inline mr-2 -mt-0.5" />History Check
              </h2>
              <p className="text-sm text-zinc-500 font-body mb-3">
                Check Wayback Machine history for Asian languages, casino & adult content
              </p>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="history-check-form">
                <textarea
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                  placeholder={"example.com\nmydomain.net\ncoolsite.io"}
                  className="w-full h-36 font-mono text-sm border border-zinc-300 rounded-sm p-3 focus:ring-2 focus:ring-blue-600 focus:outline-none resize-y bg-white"
                  data-testid="history-domain-input"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400">{domainsText.split('\n').filter(d => d.trim()).length} domain(s)</span>
                  <Button type="submit" disabled={submitting || !domainsText.trim()} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-5 h-9" data-testid="history-check-button">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</> : <><Shield className="w-4 h-4 mr-2" /> Check History</>}
                  </Button>
                </div>
              </form>
            </div>

            {/* History */}
            <div className="border border-zinc-200 bg-white" data-testid="history-checks-list">
              <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">History</span>
              </div>
              <ScrollArea className="max-h-[400px]">
                {checks.length === 0 ? (
                  <p className="p-4 text-xs text-zinc-400 font-body">No checks yet</p>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {checks.map(c => (
                      <div
                        key={c.id}
                        onClick={() => setSelectedCheckId(c.id)}
                        role="button"
                        tabIndex={0}
                        className={`w-full text-left p-3 hover:bg-zinc-50 transition-colors cursor-pointer ${selectedCheckId === c.id ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}
                        data-testid={`history-item-${c.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <StatusBadge status={c.status} />
                          <div className="flex gap-1">
                            {c.status === 'completed' && c.clean_count > 0 && (
                              <span onClick={(e) => { e.stopPropagation(); setSelectedCheckId(c.id); }} role="button" tabIndex={0} className="text-zinc-400 hover:text-blue-600 cursor-pointer" data-testid={`download-history-${c.id}`}><Download className="w-3.5 h-3.5" /></span>
                            )}
                            <span onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} role="button" tabIndex={0} className="text-zinc-400 hover:text-rose-600 cursor-pointer" data-testid={`delete-history-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></span>
                          </div>
                        </div>
                        <div className="text-xs text-zinc-600">
                          {c.total_count || 0} domains
                          {c.status === 'completed' && (
                            <span className="ml-1">
                              <span className="text-emerald-600 font-medium">{c.clean_count || 0}</span> clean
                              {c.flagged_count > 0 && <span className="text-rose-600 font-medium ml-1">{c.flagged_count} flagged</span>}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>

          {/* Right column — Results */}
          <div className="lg:col-span-2 border border-zinc-200 bg-white" data-testid="history-results">
            <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Results</span>
              {detail?.status === 'completed' && (
                <>
                  <div className="ml-auto flex gap-1">
                    {['all', 'clean', 'flagged', 'no_data'].map(f => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${
                          filter === f ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                        }`}
                        data-testid={`filter-${f}`}
                      >
                        {f === 'all' ? `All (${detail.total_count})` :
                         f === 'clean' ? `Clean (${detail.clean_count})` :
                         f === 'flagged' ? `Flagged (${detail.flagged_count})` :
                         `No Data (${detail.no_data_count})`}
                      </button>
                    ))}
                  </div>
                  {detail.clean_domains?.length > 0 && (
                    <button onClick={() => handleDownload(detail)} className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 border border-emerald-200" data-testid="download-clean-btn">
                      <Download className="w-3 h-3" /> Download Clean
                    </button>
                  )}
                </>
              )}
            </div>

            {!detail ? (
              <div className="flex flex-col items-center justify-center h-64 text-zinc-400">
                <Shield className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm font-body">Enter domains and click Check History</p>
              </div>
            ) : detail.status === 'processing' ? (
              <div className="p-8 space-y-4">
                <div className="flex items-center gap-3">
                  <Progress value={detail.progress} className="h-2 flex-1" />
                  <span className="text-sm font-mono text-zinc-500">{detail.progress}%</span>
                </div>
                <p className="text-sm text-zinc-500 font-body text-center">
                  Analyzing Wayback Machine snapshots... ({detail.downloaded_files || 0}/{detail.total_count} domains)
                </p>
              </div>
            ) : detail.status === 'failed' ? (
              <div className="p-6 text-sm text-rose-600">{detail.error || 'Check failed'}</div>
            ) : detail.status === 'completed' ? (
              <ScrollArea className="h-[calc(100vh-280px)]">
                <div data-testid="history-results-list">
                  {filteredResults.length === 0 ? (
                    <p className="p-8 text-center text-sm text-zinc-400">No results for this filter</p>
                  ) : (
                    filteredResults.map(r => (
                      <DomainDetailRow key={r.domain} result={r} />
                    ))
                  )}
                </div>
              </ScrollArea>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
